import { ActionBar } from '@/components/layout/ActionBar';
import { Button } from '@/components/ui/button';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useDeleteTableViewRowsService,
  useDownloadPapersService,
  useExecuteTableViewAnalysisService,
} from '@/services/tableViews';
import { TableViewColumnDto, TableViewRowDto } from '@ditto/shared';
import { Download, Info, RotateCcw, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

interface TableViewActionBarProps {
  open: boolean;
  clearSelection: () => void;
  columns: TableViewColumnDto[];
  rows: TableViewRowDto[];
  tableViewId: string;
  selectedRowsCount: number;
}

export function TableViewActionBar({
  open,
  clearSelection,
  columns,
  rows,
  tableViewId,
  selectedRowsCount,
}: TableViewActionBarProps) {
  const { executeAnalysis, isExecuting } = useExecuteTableViewAnalysisService();
  const { deleteTableViewRows, isPending: isDeleting } =
    useDeleteTableViewRowsService();
  const { downloadPapersAsZip, isDownloading } = useDownloadPapersService();
  const handleExecuteAnalysis = async () => {
    await executeAnalysis({
      tableViewId: tableViewId,
      data: {
        columnIds: columns.map(column => column.id),
        rowIds: rows.map(row => row.id),
      },
    });
    clearSelection();
  };

  const handleClose = () => {
    clearSelection();
  };

  const handleDeleteSelectedRows = () => {
    deleteTableViewRows({
      tableViewId,
      rowIds: rows.map(row => row.id).join(','),
    });
    toast.success('Rows deleted successfully');
    clearSelection();
  };

  const handleDownloadPapers = async () => {
    try {
      const result = await downloadPapersAsZip(rows, columns);
      if (result.downloadedCount === 0) {
        toast.info('No papers available for download');
      } else if (result.skippedCount > 0) {
        toast.info(
          `Downloaded ${result.downloadedCount} papers. ${result.skippedCount} papers were not available.`
        );
      } else {
        toast.success(`Downloaded ${result.downloadedCount} papers`);
      }
    } catch {
      toast.error('Failed to download papers');
    }
  };

  const hasPaperSources = rows.some(
    row => row.source === 'pubmed' || row.source === 'openalex'
  );

  return (
    <ActionBar open={open}>
      <FlexRow gap="6" alignItems="center" justifyContent="between" w="full">
        <FlexRow gap="2" alignItems="center">
          <Button variant="link" size="icon" onClick={handleClose}>
            <X className="text-white" />
          </Button>
          <Text variant="body-sm" className="text-white">
            {selectedRowsCount > 1
              ? `${selectedRowsCount} rows selected`
              : '1 row selected'}
          </Text>
        </FlexRow>
        <FlexRow gap="2">
          <Button
            variant="secondary"
            onClick={handleExecuteAnalysis}
            loading={isExecuting}
          >
            <RotateCcw />
            Regenerate
          </Button>
          {hasPaperSources && (
            <Button
              variant="secondary"
              onClick={handleDownloadPapers}
              loading={isDownloading}
            >
              <Download />
              Download papers
              <Tooltip delayDuration={0}>
                <TooltipTrigger
                  asChild
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <span className="inline-flex">
                    <Info className="w-3.5 h-3.5 text-neutral-500" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Downloads paper pdfs if they are publicly available
                </TooltipContent>
              </Tooltip>
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={handleDeleteSelectedRows}
            loading={isDeleting}
          >
            <Trash2 />
            Delete
          </Button>
        </FlexRow>
      </FlexRow>
    </ActionBar>
  );
}
